import Backbone from 'backbone';
import gql from 'graphql-tag';
import React, {useCallback, useEffect, useState} from 'react';
import ReactDOM from 'react-dom';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';

import {
  ReviewHistoryTabViewQuery,
  ReviewHistoryTabViewQueryVariables,
} from './__generated/ReviewHistoryTabViewQuery';
import {RecentActivity} from './Review';
import ReviewHistoryTabState from './ReviewHistoryTabState';

const INITIAL_ITEMS_SHOWING = 5;

/* @package exported for testing */
export const REVIEW_HISTORY_TAB_VIEW_QUERY = gql`
  query ReviewHistoryTabViewQuery($id: StrictString!) @engComponent(name: NOTEBOOK) @skipAudit {
    reviewable(id: $id) {
      ... on ReviewableInterface {
        id
        review {
          ...ReviewSummaryGraphQL
          history
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ReviewSummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/** @package exported for testing */
export function ReviewHistoryTab({fileId}: {fileId: string}): JSX.Element {
  const [historyStartIndex, setHistoryStartIndex] = useState<number | null>(null);
  const showAllHistory = useCallback(() => {
    setHistoryStartIndex(0);
  }, [setHistoryStartIndex]);
  const result = useBenchlingQuery<ReviewHistoryTabViewQuery, ReviewHistoryTabViewQueryVariables>(
    REVIEW_HISTORY_TAB_VIEW_QUERY,
    {variables: {id: fileId}, fetchPolicy: 'cache-and-network'}
  );
  useEffect(() => {
    if (result.type === QueryResultType.SUCCESS && historyStartIndex == null) {
      setHistoryStartIndex(
        !isInaccessible(result.data.reviewable) &&
          result.data.reviewable.review &&
          result.data.reviewable.review.history
          ? Math.max(0, result.data.reviewable.review.history.length - INITIAL_ITEMS_SHOWING)
          : 0
      );
    }
  }, [result, historyStartIndex]);

  if (result.type === QueryResultType.ERROR) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Unable to load review history. Try refreshing the page."
      />
    );
  } else if (result.type === QueryResultType.PENDING || historyStartIndex == null) {
    return (
      <div data-reviewable-id={fileId}>
        <CenteredHorizontalThrobber />
      </div>
    );
  } else if (isInaccessible(result.data.reviewable)) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="The review history is inaccessible." />;
  }
  return (
    <>
      <RecentActivity
        apiType={APIUtil.apiIdToType(fileId)}
        historyStartIndex={historyStartIndex}
        showAllHistory={showAllHistory}
        historyItems={result.data.reviewable.review?.history ?? []}
        fileId={fileId}
        reviewId={result.data.reviewable.review?.id}
        refetch={result.refetch}
      />
    </>
  );
}

export default class ReviewHistoryTabView extends Backbone.View<ReviewHistoryTabState> {
  static initClass(): void {
    this.prototype.className = 'buffer-tab';
  }

  override initialize(): void {
    this.listenTo(this.model, 'change:fileId', () => {
      this.rerender();
    });
  }

  override render(): ReviewHistoryTabView {
    this.rerender();
    return this;
  }

  rerender(): void {
    const fileId = this.model.get('fileId');
    renderToDOM(
      <ReviewHistoryTab
        fileId={fileId}
        // Avoid sharing state if the fileId changes: this is unexpected, but be defensive.
        key={fileId}
      />,
      this.el
    );
  }

  override remove(): Backbone.View<ReviewHistoryTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

ReviewHistoryTabView.initClass();
